
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { useState, useRef } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import { createClient } from '@/utils/supabase/component';
import Seo from '@/components/Seo';
import Turnstile, { isTurnstileEnabled, TURNSTILE_ERROR_MESSAGE, type TurnstileHandle, } from '@/components/Turnstile';
export default function ForgotPassword() {
    const supabase = createClient();
    const router = useRouter();
    const { t } = useLocale('login');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{
        type: 'error' | 'success';
        text: string;
    } | null>(null);
    // Password recovery is captcha-protected too once Supabase Attack
    // Protection is on.
    const captchaRef = useRef<TurnstileHandle>(null);
    const handlePasswordReset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) {
            setMessage({ type: 'error', text: 'Please enter your email address' });
            return;
        }
        setLoading(true);
        setMessage(null);
        try {
            const captchaToken = await captchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setMessage({ type: 'error', text: t(TURNSTILE_ERROR_MESSAGE) });
                return;
            }
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/auth/confirm?type=recovery&next=/auth/reset-password`,
                captchaToken,
            });
            if (error) {
                setMessage({ type: 'error', text: error.message });
            }
            else {
                setMessage({
                    type: 'success',
                    text: "Check your email for the password reset link. If you don't see it, please check your spam folder.",
                });
            }
        }
        catch (error) {
            setMessage({ type: 'error', text: 'An unexpected error occurred' });
        }
        finally {
            // Turnstile tokens are single use — clear it whatever the outcome.
            captchaRef.current?.reset();
            setLoading(false);
        }
    };
    return (<div className='flex flex-col items-center justify-center min-h-screen py-12 dark:bg-slate-900'>
      <Seo templateTitle='Forgot Password'/>
      <div className='w-full max-w-md'>
        <div>
          <Link href='/' className='' passHref legacyBehavior>
            <span className='flex justify-center text-2xl font-extrabold text-primary-700'>
              <Image src='/images/marky.png' width='250' height='36' alt='Easy-Peasy.ai logo'/>
            </span>
          </Link>
          <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-slate-200'>
            {t('Forgot your password?')}
          </h2>
          <p className='mt-2 text-center text-sm text-gray-600 dark:text-slate-400'>
            {t("Enter your email and we'll send you a link to reset your password")}
          </p>
        </div>

        <div className='mt-8'>
          {message && (<div className={`mb-4 p-4 rounded-md ${message.type === 'error'
                ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                : 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400'}`}>
              {message.text}
            </div>)}

          <form onSubmit={handlePasswordReset} className='space-y-6'>
            <div>
              <label htmlFor='email' className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
                {t('Email address')}
              </label>
              <input id='email' name='email' type='email' autoComplete='email' required value={email} onChange={(e) => setEmail(e.target.value)} className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm' placeholder={t('Your email address')}/>
            </div>

            <Turnstile ref={captchaRef} action='password-reset' className='flex justify-center'/>

            <div>
              <button type='submit' disabled={loading} className='flex w-full justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'>
                {loading ? t('Sending reset link...') : t('Send reset link')}
              </button>
            </div>
          </form>

          <div className='mt-6 text-center space-y-2'>
            <div>
              <Link href='/login' className='text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400'>
                {t('Back to login')}
              </Link>
            </div>
            <div>
              <Link href='/login#auth-sign-up' className='text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400'>
                {t("Don't have an account? Sign up")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>);
}

    async function __Next_Translate__getStaticProps__1a11b03d1c1__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/auth/forgot-password',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a11b03d1c1__ as getStaticProps }
  